import type { ReactNode } from "react";
import { useAccount } from "wagmi";
import { EthTokenIcon, GameProvider, Text, TokenYoloIcon, WinnerCrownIcon } from "@looksrare/uikit";
import { ChainId } from "@looksrare/config";
import {
  ContractVersion,
  YoloConfig,
  YoloContractName,
  getYoloContractNameFromNetworkAndVersion,
} from "@looksrare/yolo";
import { WarningModal } from "@looksrare/yolo/components/modals/WarningModal";
import { PotItemsList } from "@looksrare/yolo/components/round/PotItemsList";
import {
  useClaimPrizes,
  useDeposit,
  useReadOracleSignatureValidityPeriod,
  useRollOverEthToNextRound,
  useDepositEthIntoMultipleRounds,
  useWithdrawDeposits,
} from "@looksrare/yolo/utils/contract";
import { AddErc20 } from "@looksrare/yolo/components/depositAssets/AddPanel/AddErc20";
import { StepSendToPotsMultipleRound } from "@looksrare/yolo/components/modals/EnterYoloModal/StepSendToPotsMultipleRounds";
import { GemMessage } from "@looksrare/yolo/components/round/points/GemMessage";
import { isBefore } from "date-fns/isBefore";
import { Help } from "@/components/gamesCustomisations/yolo/Help";
import { gameAddresses } from "@/config";
import { CenterActiveMain } from "@/components/gamesCustomisations/yolo/CenterActiveMain";
import { YoloPointsExplanationModal } from "@/components/gamesCustomisations/yolo/YoloPointsExplanationModal";
import { getUserColor } from "@/components/gamesCustomisations/yolo/colors";
import { useGetClaimPrizesPaymentRequired } from "@/components/gamesCustomisations/yolo/contract";
import { PlayerViewProfileCTA } from "@/components/gamesCustomisations/yolo/PlayerViewProfileCTA";
import { useUser } from "@/queries/user";
import { useGlobalSettings } from "@/hooks";
import { CenterWinnerAvatar } from "./gamesCustomisations/yolo/CenterWinnerAvatar";

export const YoloGamesYoloProvider = ({ children }: { children: ReactNode }) => {
  const isSoundEnabled = useGlobalSettings((state) => state.isSoundEnabled);
  const { address } = useAccount();
  const { data: user } = useUser(address!, { enabled: !!address });

  return (
    <GameProvider<YoloConfig>
      widgetBotOptions={{
        server: "1204359490925764680",
        channel: "1204997140297027696",
        shard: "https://emerald.widgetbot.io",
        glyph: ["/images/win/chat.png", "32px"],
        color: "#CCFD07",
        notifications: false,
        indicator: true,
      }}
      supportedNetworks={["blast"]}
      addresses={gameAddresses}
      fallbackChainId={ChainId.BLAST_MAINNET}
      config={{
        basePath: "yolo",
        referenceToken: {
          symbol: "ETH",
          icon: EthTokenIcon,
          decimals: 18,
          name: "Ethereum",
          minDeposit: 0.01,
          maxDisplayDecimals: 2,
          maxPrecision: 2,
        },
        getUserColor,
        hasBackgroundImage: true,
        // Accounts for left nav
        // Note: caused by breakpoint mismatch
        mobileStickyBarProps: { width: { base: "100%", md: "calc(100% - 5rem)" }, right: 0, left: undefined },
        supportedTokens: ["ETH", "YOLO", "BLOOKS", "USDB", "BLAST", "PAC", "ORBIT"],
        useGlobalMuteButton: true,
        supportsMetrics: true,
        isRolloverEnabled: true,
        depositPresets: ["0.01", "0.05", "0.1"],
        points: {
          enabled: false,
          bonusPoints: 500,
          hideEnterNowMultiplier: false,
          hideMultiplierInRoundOverview: true,
          label: "Pts",
          Icon: null,
          accentColor: "interactive-03",
          bgGradient: "transparent",
        },
        roulette: {
          CenterActiveMain,
        },
        round: {
          ctaArea: {
            props: {
              borderColor: "interactive-03",
              borderWidth: "1px",
            },
          },
        },
        EyeCatchingText: Text,
        WarningModal,
        supportNfts: false,
        supportErc20: true,
        supportNativeToken: true,
        removeRoundCtaBg: true,
        supportsUserProfiles: false,
        hideLogoInCart: true,
        defaultDepositContractName: "ETH",
        TransferManagerIcon: TokenYoloIcon,
        contract: {
          version: ContractVersion.V2,
          // This project uses all defaults
          identifier: "YOLO_V2",
          useDeposit,
          useDepositEthIntoMultipleRounds,
          useClaimPrizes,
          useReadOracleSignatureValidityPeriod,
          useGetClaimPrizesPaymentRequired,
          useRollOverEthToNextRound,
          useWithdrawDeposits,
          getYoloContractNameFromNetworkAndVersion,
          getYoloContractNamesFromNetwork: () => [YoloContractName.YOLO_V2_BLAST],
        },
        AddErc20,
        PotItemsList,
        StepSendToPotsMultipleRound,
        GemMessage,
        YoloPointsExplanationModal,
        Help,
        WinnerIcon: WinnerCrownIcon,
        CenterWinnerAvatar,
        PlayerViewProfileCTA,
        selfTimeoutUntil: user?.selfTimeoutUntil,
        isWalletBlocked: !!user?.selfTimeoutUntil && isBefore(Date.now(), user.selfTimeoutUntil),
        isMuted: !isSoundEnabled,
      }}
    >
      {children}
    </GameProvider>
  );
};
